// AwardMoneyPanel.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import { Bars, FeedCard, FeedSection, FeedSegments, PanelState } from '../feed/FeedCard';
import {
  AWARD_GROUPS,
  awardGroupLabel,
  awardTypeLabel,
  currentFiscalYear,
  fetchAwardOverview,
  formatDollars,
  formatPercent,
  recipientLabel,
  titleCaseName,
} from '../../utils/spending';

/**
 * AwardMoneyPanel
 *
 * Where the award money goes this fiscal year: by type of award, the agencies
 * awarding it, who received it, and -- for contracts -- what was bought.
 *
 * A different picture from the budget above it, and the caption says so:
 * award data covers only money paid out to a named contractor, grantee,
 * borrower or beneficiary. The two are never added.
 *
 * Three traps from the guide are handled where they would show:
 *
 *   - MULTIPLE RECIPIENTS is relabelled; it is not an organisation.
 *   - Loans are subsidy cost and can be negative; the label says so, and a
 *     negative row gets its signed figure and no bar.
 *   - top_purchases is contracts only, and its share is of whatever group
 *     was asked for, so under "All" a large contract category reads as 0.7%.
 *     It is shown only with the Contracts group selected.
 */

const AWARD_TYPE_ORDER = ['direct', 'grant', 'contract', 'idv', 'loan', 'other'];

function AwardMoneyPanel() {
  const { t } = useTranslation('common');
  const [group, setGroup] = useState('all');
  const overview = useApi2Resource(() => fetchAwardOverview(group), [group]);

  const data = overview.data;
  const fiscalYear = data?.fiscal_year || currentFiscalYear();

  const byType = data?.by_award_type
    ? AWARD_TYPE_ORDER.filter((type) => typeof data.by_award_type[type] === 'number').map((type) => ({
        name: awardTypeLabel(type, t),
        amount: data.by_award_type[type],
      }))
    : [];

  const agencies = Array.isArray(data?.top_funding_agencies) ? data.top_funding_agencies : [];
  const recipients = Array.isArray(data?.top_recipients) ? data.top_recipients : [];
  const purchases =
    group === 'contracts' && Array.isArray(data?.top_purchases) ? data.top_purchases : [];

  const empty = !overview.loading && !overview.error && !byType.length && !agencies.length;

  return (
    <FeedSection
      id="awards"
      title={t('common:awardMoneyTitle', 'Where the award money goes')}
      subtitle={
        data
          ? t(
              'common:awardMoneySubtitle',
              '{{amount}} obligated in awards in FY{{year}} so far. Award data covers only money paid to a named contractor, grantee, borrower or beneficiary, so it is not added to the budget above.',
              { amount: formatDollars(data.total_obligations), year: fiscalYear }
            )
          : null
      }
    >
      <FeedCard>
        <FeedSegments
          className="spending-card-tabs"
          label={t('common:awardGroup', 'Kind of award')}
          value={group}
          onChange={setGroup}
          options={AWARD_GROUPS.map((id) => ({ id, label: awardGroupLabel(id, t) }))}
        />

        <PanelState loading={overview.loading} error={overview.error} empty={empty} />

        {group === 'all' && byType.length > 0 && (
          <div className="spending-list">
            <h3 className="spending-list-title">{t('common:byAwardType', 'By type of award')}</h3>
            <Bars rows={byType} tone="kind" format={(value) => formatDollars(value)} share={() => null} />
          </div>
        )}

        {agencies.length > 0 && (
          <div className="spending-list">
            <h3 className="spending-list-title">
              {t('common:fundingAgencies', 'Agencies awarding it')}
            </h3>
            <Bars
              rows={agencies}
              tone="gov"
              name={(row) => row.name}
              format={(value) => formatDollars(value)}
              sub={(row) => formatPercent(row.share)}
            />
          </div>
        )}

        {recipients.length > 0 && (
          <div className="spending-list">
            <h3 className="spending-list-title">{t('common:recipientsPaid', 'Recipients')}</h3>
            <Bars
              rows={recipients}
              tone="dep"
              name={(row) => titleCaseName(recipientLabel(row, t))}
              format={(value) => formatDollars(value)}
              sub={(row) => formatPercent(row.share)}
            />
          </div>
        )}

        {purchases.length > 0 && (
          <div className="spending-list">
            <h3 className="spending-list-title">{t('common:purchases', 'What was bought')}</h3>
            <Bars
              rows={purchases}
              tone="kind"
              name={(row) => titleCaseName(row.name)}
              format={(value) => formatDollars(value)}
              sub={(row) =>
                t('common:shareOfContracts', '{{share}} of contract obligations', {
                  share: formatPercent(row.share),
                })
              }
            />
          </div>
        )}
      </FeedCard>
    </FeedSection>
  );
}

export default AwardMoneyPanel;